import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ArrowRight } from "lucide-react";
import { AdminShell } from "../components/admin/AdminShell";
import { API, fmtEventDate, fmtPrice } from "../lib/eventUtils";

const RANGES = [
  ["all", "All Time"],
  ["7", "7 Days"],
  ["30", "30 Days"],
  ["90", "90 Days"],
  ["custom", "Custom"],
];

const daysAgo = (n) => new Date(Date.now() - n * 86400000).toISOString().slice(0, 10);

const Card = ({ label, value, sub, testid }) => (
  <div className="border border-white/10 bg-[#0c0c0c] p-5" data-testid={testid}>
    <p className="font-mono2 text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-2">{label}</p>
    <p className="font-head font-black uppercase text-3xl text-white leading-none">{value}</p>
    {sub && <p className="font-mono2 text-[10px] uppercase tracking-[0.15em] text-zinc-600 mt-2">{sub}</p>}
  </div>
);

export default function AdminOverview() {
  const [range, setRange] = useState("all");
  const [custom, setCustom] = useState({ start: "", end: "" });
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);

  const load = useCallback(async (start, end) => {
    setLoading(true);
    setError("");
    try {
      const params = {};
      if (start) params.start = start;
      if (end) params.end = end;
      const res = await axios.get(`${API}/api/admin/overview`, { params, withCredentials: true });
      setData(res.data);
    } catch (e) {
      setError(e.response?.data?.detail || "Could not load the overview");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (range === "all") load();
    else if (range !== "custom") load(daysAgo(Number(range)));
  }, [range, load]);

  return (
    <AdminShell title="Overview">
      <div data-testid="admin-overview-page">
        <div className="flex flex-wrap items-center gap-2 mb-6" data-testid="overview-range-filters">
          {RANGES.map(([key, label]) => (
            <button
              key={key}
              onClick={() => setRange(key)}
              className={`font-mono2 text-[10px] uppercase tracking-[0.2em] px-3.5 py-2 border transition-colors ${
                range === key ? "border-volt/70 text-volt bg-volt/5" : "border-white/15 text-zinc-400 hover:text-white"
              }`}
              data-testid={`overview-range-${key}`}
            >
              {label}
            </button>
          ))}
          {range === "custom" && (
            <span className="flex items-center gap-2">
              <input type="date" value={custom.start} onChange={(e) => setCustom({ ...custom, start: e.target.value })} className="volt-input !w-auto !py-1.5 !text-xs" data-testid="overview-custom-start" />
              <span className="text-zinc-600 text-xs">to</span>
              <input type="date" value={custom.end} onChange={(e) => setCustom({ ...custom, end: e.target.value })} className="volt-input !w-auto !py-1.5 !text-xs" data-testid="overview-custom-end" />
              <button onClick={() => load(custom.start || undefined, custom.end || undefined)} className="btn-volt !py-1.5 !px-4 !text-xs" data-testid="overview-custom-apply">
                Apply
              </button>
            </span>
          )}
        </div>

        {error && <p className="border border-red-500/40 bg-red-500/10 text-red-300 text-sm px-4 py-3 mb-6" data-testid="overview-error">{error}</p>}
        {loading && !data && <p className="font-mono2 text-xs uppercase tracking-[0.25em] text-zinc-500 animate-pulse" data-testid="overview-loading">Loading…</p>}

        {data && (
          <>
            <div className={`grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-5 gap-px bg-white/10 border border-white/10 mb-10 ${loading ? "opacity-60" : ""}`} data-testid="overview-cards">
              <Card label="Signups" value={data.signups} testid="overview-card-signups" />
              <Card label="Revenue" value={fmtPrice(data.revenue_cents)} sub={`${data.redemptions} code redemption${data.redemptions === 1 ? "" : "s"}`} testid="overview-card-revenue" />
              <Card label="Upcoming Events" value={data.upcoming_events} testid="overview-card-upcoming" />
              <Card label="Spots Remaining" value={data.remaining_spots} sub="across upcoming events" testid="overview-card-spots" />
              <Card label="Waitlist" value={data.waitlist} testid="overview-card-waitlist" />
              <Card label="Products" value={`${data.products_active}/${data.products_total}`} sub="active / total" testid="overview-card-products" />
              <Card label="Low Stock" value={data.low_stock} sub="active products at/below threshold" testid="overview-card-lowstock" />
              <Card label="Affiliate Codes" value={data.affiliates_active} sub="active" testid="overview-card-affiliates" />
            </div>

            <div className="grid lg:grid-cols-2 gap-8">
              <div data-testid="overview-upcoming-progress">
                <p className="eyebrow mb-4">Upcoming Event Progress</p>
                {data.upcoming.length === 0 ? (
                  <p className="text-zinc-600 text-sm border border-white/10 bg-[#0c0c0c] p-6" data-testid="overview-upcoming-empty">No upcoming published events.</p>
                ) : (
                  <div className="space-y-4">
                    {data.upcoming.map((ev) => {
                      const pct = Math.min((ev.registered_count / ev.capacity) * 100, 100);
                      return (
                        <div key={ev.slug} className="border border-white/10 bg-[#0c0c0c] p-5" data-testid={`overview-upcoming-${ev.slug}`}>
                          <div className="flex items-center justify-between gap-3 mb-2">
                            <Link to={`/events/${ev.slug}`} className="font-head font-bold uppercase text-base hover:text-volt transition-colors truncate">
                              {ev.title}
                            </Link>
                            <span className="font-mono2 text-[10px] uppercase tracking-[0.15em] text-volt shrink-0">
                              {ev.registered_count}/{ev.capacity}
                            </span>
                          </div>
                          <p className="font-mono2 text-[10px] uppercase tracking-[0.15em] text-zinc-500 mb-3">{fmtEventDate(ev.date_start)}</p>
                          <div className="h-1.5 bg-white/10"><div className="h-full bg-volt" style={{ width: `${pct}%` }} /></div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              <div data-testid="overview-recent-signups">
                <div className="flex items-center justify-between mb-4">
                  <p className="eyebrow">Recent Signups</p>
                  <Link to="/admin/signups" className="inline-flex items-center gap-1.5 text-volt font-mono2 text-[10px] uppercase tracking-[0.2em] hover:gap-2.5 transition-all" data-testid="overview-all-signups-link">
                    All Signups <ArrowRight size={12} />
                  </Link>
                </div>
                {data.recent_signups.length === 0 ? (
                  <p className="text-zinc-600 text-sm border border-white/10 bg-[#0c0c0c] p-6" data-testid="overview-recent-empty">No signups yet.</p>
                ) : (
                  <div className="border border-white/10 bg-[#0c0c0c] divide-y divide-white/5">
                    {data.recent_signups.map((r, i) => (
                      <div key={i} className="px-5 py-3 flex items-center justify-between gap-3 text-sm" data-testid={`overview-recent-row-${i}`}>
                        <div className="min-w-0">
                          <p className="font-semibold text-white truncate">{r.first_name} {r.last_name}</p>
                          <p className="text-zinc-500 text-xs truncate">{r.email} · {r.event_title}</p>
                        </div>
                        <div className="text-right shrink-0">
                          {r.amount_paid_cents ? <p className="text-volt font-mono2 text-xs">{fmtPrice(r.amount_paid_cents)}</p> : <p className="text-zinc-600 font-mono2 text-[10px] uppercase">{r.source}</p>}
                          <p className="text-zinc-600 text-[10px] font-mono2">{(r.created_at || "").slice(0, 10)}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </AdminShell>
  );
}
